import React from 'react'
import type { ConversationPlan } from '@knownow/domain'
import { useSelectionStore } from '@/stores/selection-store'

export interface PreviewPanelProps {
  readonly plan: ConversationPlan | null
  readonly loading: boolean
}

const FORMAT_LABELS: Record<string, string> = {
  open: 'Open',
  widget: 'Widget',
  both: 'Both',
}

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-error-light text-error-dark',
  medium: 'bg-warning-light text-warning-dark',
  low: 'bg-info-light text-info-dark',
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  reviewing: 'Reviewing',
  active: 'Active',
  completed: 'Completed',
  archived: 'Archived',
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-neutral-200 text-neutral-700',
  reviewing: 'bg-warning-light text-warning-dark',
  active: 'bg-success-light text-success-dark',
  completed: 'bg-info-light text-info-dark',
  archived: 'bg-neutral-200 text-neutral-500',
}

export function PreviewPanel({ plan, loading }: PreviewPanelProps): React.ReactElement {
  const selection = useSelectionStore((s) => s.selection)
  const select = useSelectionStore((s) => s.select)

  const activeNeeds = plan?.informationNeeds.filter((n) => n.deletedAt === null) ?? []

  const handleNeedSelect = (needId: string): void => {
    const need = activeNeeds.find((n) => n.id === needId)
    if (need) {
      select({
        informationNeedId: need.id,
        informationNeedDescription: need.description,
        field: null,
      })
    }
  }

  return (
    <div className="flex h-full flex-col" data-testid="preview-panel">
      <div className="flex-1 overflow-y-auto bg-neutral-50 p-6">
        {loading && (
          <div className="flex items-center justify-center py-12" data-testid="preview-loading">
            <p className="text-sm text-neutral-500">Loading plan...</p>
          </div>
        )}

        {!loading && !plan && (
          <p className="text-sm text-neutral-500" data-testid="empty-preview">
            No plan found. Start a conversation to build your research plan.
          </p>
        )}

        {!loading && plan && (
          <div
            className="mx-auto max-w-2xl"
            data-testid="plan-container"
          >
            {/* Plan header */}
            <div className="mb-6" data-testid="plan-header">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold font-serif text-neutral-900">
                  {plan.title || 'Untitled Plan'}
                </h1>
                <span
                  className={[
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    STATUS_COLORS[plan.status] ?? 'bg-neutral-200 text-neutral-700',
                  ].join(' ')}
                  data-testid="plan-status"
                >
                  {STATUS_LABELS[plan.status] ?? plan.status}
                </span>
              </div>
              {plan.description && (
                <p className="mt-1 text-base text-neutral-600">
                  {plan.description}
                </p>
              )}
              {plan.timeBudgetMinutes > 0 && (
                <p className="mt-1 text-xs text-neutral-500">
                  Time budget: {plan.timeBudgetMinutes} minutes
                </p>
              )}
            </div>

            {/* Information needs list */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">
                Information Needs
              </h3>

              {activeNeeds.length === 0 && (
                <p className="text-sm text-neutral-500" data-testid="empty-needs">
                  No information needs yet. Start a conversation to define what you want to learn.
                </p>
              )}

              <div className="flex flex-col gap-3" data-testid="needs-list">
                {activeNeeds.map((need, index) => (
                  <button
                    key={need.id}
                    type="button"
                    onClick={() => handleNeedSelect(need.id)}
                    className={[
                      'w-full text-left rounded-lg border p-4 transition-colors',
                      selection?.informationNeedId === need.id
                        ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                        : 'border-neutral-200 bg-neutral-0 hover:border-neutral-300',
                    ].join(' ')}
                    data-testid={`need-${need.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neutral-200 text-neutral-600 text-xs font-medium flex items-center justify-center">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-neutral-900">{need.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
                            {FORMAT_LABELS[need.collectionFormat] ?? need.collectionFormat}
                          </span>
                          <span
                            className={[
                              'rounded-full px-2 py-0.5 text-xs font-medium',
                              PRIORITY_COLORS[need.priority] ?? 'bg-neutral-100 text-neutral-600',
                            ].join(' ')}
                          >
                            {need.priority}
                          </span>
                          {need.category && (
                            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
                              {need.category}
                            </span>
                          )}
                          {need.widgetConfig && (
                            <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                              {need.widgetConfig.type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Conversation strategy */}
            <div data-testid="strategy-section">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">
                Conversation Strategy
                {plan.strategy?.isStale && (
                  <span className="ml-2 text-xs font-normal text-warning-base" data-testid="stale-warning">
                    (Stale — needs regeneration)
                  </span>
                )}
              </h3>

              {!plan.strategy && (
                <p className="text-sm text-neutral-500" data-testid="empty-strategy">
                  No strategy yet. The AI will generate one after reviewing your information needs.
                </p>
              )}

              {plan.strategy && (
                <div className="rounded-lg border border-neutral-200 bg-neutral-0 p-4 space-y-3">
                  <div>
                    <p className="text-xs font-medium text-neutral-500 mb-1">Approach</p>
                    <p className="text-sm text-neutral-900">{plan.strategy.approach}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-neutral-500 mb-1">Opening</p>
                    <p className="text-sm text-neutral-900">{plan.strategy.opening}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-neutral-500 mb-1">Transitions</p>
                    <p className="text-sm text-neutral-900">{plan.strategy.transitions}</p>
                  </div>
                  {Object.keys(plan.strategy.timeAllocation).length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-neutral-500 mb-1">Time Allocation</p>
                      <div className="flex gap-2">
                        {Object.entries(plan.strategy.timeAllocation).map(([key, value]) => (
                          <span key={key} className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                            {key}: {value}%
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
